/**
 * Browser client for the passkey (WebAuthn) endpoints — sibling of auth-api.js.
 *
 * ALL backend response shapes for /api/auth/passkey/* live in THIS file (shape
 * isolation: a contract change is a one-file fix). Ceremony mechanics come from
 * the vendored @simplewebauthn/browser (scripts/vendor/simplewebauthn-browser.js).
 *
 * Availability model — two tiers:
 *   isPasskeySupported()      sync, browser capability only.
 *   getPasskeyAvailability()  async + module-cached: also asks the backend.
 *     'unavailable' when the browser lacks WebAuthn, the RP rejects this
 *     origin (400 passkey_unsupported — e.g. the legacy pages.dev origin),
 *     or the local proxy stub answers reason:'local-dev'. EVERY passkey
 *     button (sign-in screen, mid-session modal, settings) gates on this so
 *     hiding is consistent everywhere.
 */

import { authFetch, isRealUser } from './auth-api.js';
import { rememberAuthSession, TemplateApiError } from './template-api.js';
import { startRegistration, startAuthentication } from '../vendor/simplewebauthn-browser.js';

/** Thrown when the user dismisses the OS passkey sheet — callers swallow it. */
export class PasskeyCancelledError extends Error {
  constructor() {
    super('Passkey prompt was dismissed');
    this.name = 'PasskeyCancelledError';
  }
}

/** Browser capability only (no network). */
export function isPasskeySupported() {
  return typeof window !== 'undefined' && Boolean(window.PublicKeyCredential);
}

let _availability = null; // 'available' | 'unavailable' | null (unknown)
let _availabilityProbe = null;

function markUnavailable() {
  _availability = 'unavailable';
}

function looksUnsupported(data, status) {
  if (status === 400 && data?.code === 'passkey_unsupported') return true;
  if (data && data.ok === false && data.reason === 'local-dev') return true;
  return false;
}

/**
 * Backend-aware availability, cached for the session. Probes
 * authentication-options once (cheap, public; the minted challenge simply
 * expires unused).
 * @returns {Promise<'available'|'unavailable'>}
 */
export function getPasskeyAvailability() {
  if (!isPasskeySupported()) return Promise.resolve('unavailable');
  if (_availability) return Promise.resolve(_availability);
  if (_availabilityProbe) return _availabilityProbe;
  _availabilityProbe = (async () => {
    try {
      const resp = await authFetch('/api/auth/passkey/authentication-options', {
        method: 'POST',
        body: JSON.stringify({})
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data?.options?.challenge) {
        _availability = 'available';
      } else {
        _availability = 'unavailable';
      }
    } catch {
      // Network failure ≠ definitively unavailable, but hiding beats a dead
      // button; a later successful call can never run anyway while offline.
      _availability = 'unavailable';
    }
    _availabilityProbe = null;
    return _availability;
  })();
  return _availabilityProbe;
}

async function parsePasskeyResponse(resp) {
  const data = await resp.json().catch(() => ({}));
  if (looksUnsupported(data, resp.status)) {
    markUnavailable();
    throw new TemplateApiError(data.error || 'Passkeys are not available here', resp.status, data);
  }
  if (!resp.ok || data.ok === false) {
    throw new TemplateApiError(
      data.error || resp.statusText || `HTTP ${resp.status}`,
      resp.status,
      data
    );
  }
  return data;
}

/** Map WebAuthn ceremony rejections: user-dismissal → PasskeyCancelledError. */
function normalizeCeremonyError(err) {
  const name = err?.name || '';
  if (name === 'NotAllowedError' || name === 'AbortError') return new PasskeyCancelledError();
  if (err?.code === 'ERROR_CEREMONY_ABORTED') return new PasskeyCancelledError();
  return err;
}

/**
 * Discoverable passkey-first sign-in. Mints the SAME session as password login
 * (body + pb_token cookie handled server-side; Bearer token remembered here).
 * @param {{signal?: AbortSignal}} [opts]
 * @returns {Promise<{ok:true, sub:string, email:string, authMode:'jwt', token:string}>}
 */
export async function signInWithPasskey({ signal } = {}) {
  const optionsResp = await authFetch('/api/auth/passkey/authentication-options', {
    method: 'POST',
    body: JSON.stringify({})
  });
  const { options, handle } = await parsePasskeyResponse(optionsResp);

  let assertion;
  try {
    assertion = await startAuthentication({ optionsJSON: options, signal });
  } catch (err) {
    throw normalizeCeremonyError(err);
  }

  const verifyResp = await authFetch('/api/auth/passkey/authentication-verify', {
    method: 'POST',
    body: JSON.stringify({ handle, response: assertion })
  });
  const data = await parsePasskeyResponse(verifyResp);
  rememberAuthSession(data);
  return data;
}

/**
 * Register a passkey for the signed-in account.
 * @param {{signal?: AbortSignal, label?: string}} [opts]
 * @returns {Promise<{id:string, label:string|null, createdAt:number}>}
 */
export async function registerPasskey({ signal, label } = {}) {
  const optionsResp = await authFetch('/api/auth/passkey/registration-options', {
    method: 'POST',
    body: JSON.stringify({})
  });
  const { options, handle } = await parsePasskeyResponse(optionsResp);

  let attestation;
  try {
    attestation = await startRegistration({ optionsJSON: options, signal });
  } catch (err) {
    throw normalizeCeremonyError(err);
  }

  const verifyResp = await authFetch('/api/auth/passkey/registration-verify', {
    method: 'POST',
    body: JSON.stringify({
      handle,
      response: attestation,
      label: label || defaultDeviceLabel()
    })
  });
  const data = await parsePasskeyResponse(verifyResp);
  return data.passkey;
}

/**
 * @returns {Promise<Array<{id:string,label:string|null,transports:string[]|null,createdAt:number,lastUsedAt:number|null}>>}
 */
export async function listPasskeys() {
  const resp = await authFetch('/api/auth/passkey/list', { method: 'GET' });
  const data = await parsePasskeyResponse(resp);
  return data.passkeys || [];
}

export async function deletePasskey(id) {
  const resp = await authFetch(`/api/auth/passkey/${encodeURIComponent(id)}`, { method: 'DELETE' });
  return parsePasskeyResponse(resp);
}

/** A friendly default label for a newly registered device. */
function defaultDeviceLabel() {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const platform = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua)
      ? 'iPad'
      : /Macintosh|Mac OS X/.test(ua)
        ? 'Mac'
        : /Windows/.test(ua)
          ? 'Windows PC'
          : /Android/.test(ua)
            ? 'Android device'
            : 'This device';
  return `${platform} passkey`;
}

export { isRealUser };
